<template>
	<v-dialog :value="value" max-width="640" scrollable @input="$emit('input', $event)">
		<v-card>
			<v-card-title class="d-flex align-center">
				<span>Verifica connessione {{ title }}</span>
				<v-spacer></v-spacer>
				<v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
			</v-card-title>
			<v-divider></v-divider>

			<v-card-text class="pt-4">
				<div v-if="loading" class="text-center py-8">
					<v-progress-circular indeterminate color="primary"></v-progress-circular>
					<div class="mt-3 grey--text">Test in corso sulle API di {{ title }}…</div>
				</div>

				<v-alert v-else-if="error" type="error" text dense>{{ error }}</v-alert>

				<template v-else-if="result">
					<v-alert :type="summary.type" text dense class="mb-4">{{ summary.text }}</v-alert>

					<v-list dense class="py-0">
						<v-list-item v-for="item in result.checks" :key="item.key" class="px-0 align-start">
							<v-list-item-icon class="mr-3 mt-3">
								<v-icon :color="statusMeta(item.status).color">{{ statusMeta(item.status).icon }}</v-icon>
							</v-list-item-icon>
							<v-list-item-content>
								<v-list-item-title class="font-weight-bold">{{ item.label }}</v-list-item-title>
								<div class="body-2">{{ item.message }}</div>
								<div v-if="item.hint" class="caption primary--text mt-1">{{ item.hint }}</div>
							</v-list-item-content>
						</v-list-item>
					</v-list>

					<div class="caption grey--text mt-2">
						Eseguita {{ $moment(result.checkedAt).format('DD/MM/YYYY HH:mm:ss') }}<span v-if="result.cached"> (risultato recente in cache: i test si ripetono dopo 60 secondi)</span>
					</div>
				</template>
			</v-card-text>

			<v-divider></v-divider>
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="$emit('input', false)">Chiudi</v-btn>
				<v-btn depressed color="primary" :loading="loading" @click="run">Ripeti verifica</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapMutations } from 'vuex';

// endpoint della verifica per ogni servizio: i marketplace stanno nella route della loro integrazione,
// gli altri (AI / social, framework, comunicazione, corrieri, finance, pagamenti, notifiche, SEO, email, statistiche, login social) in quella dei servizi
const SERVICES = {
	amazon: { title: 'Amazon', url: 'amazoneuwest1/connection/check' },
	ebay: { title: 'eBay', url: 'ebay/connection/check' },
	openai: { title: 'OpenAI', url: 'services/connection/check/openai' },
	facebook: { title: 'Facebook', url: 'services/connection/check/facebook' },
	instagram: { title: 'Instagram', url: 'services/connection/check/instagram' },
	aiassistent: { title: 'AI Assistent', url: 'services/connection/check/aiassistent' },
	whatsapp: { title: 'WhatsApp', url: 'services/connection/check/whatsapp' },
	shopify: { title: 'Shopify', url: 'services/connection/check/shopify' },
	fattureInCloud: { title: 'Fatture in Cloud', url: 'services/connection/check/fattureInCloud' },
	spedisciOnline: { title: 'Spediamo', url: 'services/connection/check/spedisciOnline' },
	gls: { title: 'GLS', url: 'services/connection/check/gls' },
	bartolini: { title: 'BRT', url: 'services/connection/check/bartolini' },
	dhl: { title: 'DHL', url: 'services/connection/check/dhl' },
	ups: { title: 'UPS', url: 'services/connection/check/ups' },
	fedex: { title: 'Fedex', url: 'services/connection/check/fedex' },
	paypal: { title: 'Paypal', url: 'services/connection/check/paypal' },
	paypalcheckout: { title: 'Paypal Checkout', url: 'services/connection/check/paypalcheckout' },
	stripe: { title: 'Stripe', url: 'services/connection/check/stripe' },
	amazonpay: { title: 'Amazon Pay', url: 'services/connection/check/amazonpay' },
	googlepay: { title: 'Google Pay', url: 'services/connection/check/googlepay' },
	scalapay: { title: 'Scalapay', url: 'services/connection/check/scalapay' },
	nexi: { title: 'Nexi', url: 'services/connection/check/nexi' },
	satispay: { title: 'Satispay', url: 'services/connection/check/satispay' },
	klarna: { title: 'Klarna', url: 'services/connection/check/klarna' },
	crypto: { title: 'Crypto.com', url: 'services/connection/check/crypto' },
	firebase_push: { title: 'Firebase Push', url: 'services/connection/check/firebase_push' },
	googletranslate: { title: 'Google Translate', url: 'services/connection/check/googletranslate' },
	googlemerchant: { title: 'Google Merchant Center', url: 'services/connection/check/googlemerchant' },
	googlesearchconsole: { title: 'Google Search Console', url: 'services/connection/check/googlesearchconsole' },
	googletrends: { title: 'Google Trends', url: 'services/connection/check/googletrends' },
	dataforseo: { title: 'DataForSEO', url: 'services/connection/check/dataforseo' },
	googleads: { title: 'Google Ads - Keyword Planner', url: 'services/connection/check/googleads' },
	metaads: { title: 'Meta Ads', url: 'services/connection/check/metaads' },
	tiktokads: { title: 'TikTok Ads', url: 'services/connection/check/tiktokads' },
	microsoftads: { title: 'Microsoft Ads', url: 'services/connection/check/microsoftads' },
	smtp: { title: 'Smtp', url: 'services/connection/check/smtp' },
	imap: { title: 'Imap', url: 'services/connection/check/imap' },
	dem4: { title: '4Dem', url: 'services/connection/check/dem4' },
	mailchimp: { title: 'Mail Chimp', url: 'services/connection/check/mailchimp' },
	brevo: { title: 'Brevo', url: 'services/connection/check/brevo' },
	getresponse: { title: 'GetResponse', url: 'services/connection/check/getresponse' },
	activecampaign: { title: 'Active Campaign', url: 'services/connection/check/activecampaign' },
	mailgun: { title: 'Mailgun', url: 'services/connection/check/mailgun' },
	sendgrid: { title: 'Sendgrid', url: 'services/connection/check/sendgrid' },
	mailup: { title: 'MailUp', url: 'services/connection/check/mailup' },
	googleMap: { title: 'Google Maps', url: 'services/connection/check/googleMap' },
	googleAnalytics: { title: 'Google Analytics', url: 'services/connection/check/googleAnalytics' },
	clarity: { title: 'Microsoft Clarity', url: 'services/connection/check/clarity' },
	googleauth: { title: 'Login con Google', url: 'services/connection/check/googleauth' },
	facebookauth: { title: 'Login con Facebook', url: 'services/connection/check/facebookauth' },
	amazonauth: { title: 'Login con Amazon', url: 'services/connection/check/amazonauth' },
	appleauth: { title: 'Accedi con Apple', url: 'services/connection/check/appleauth' },
	photoroom: { title: 'Photoroom', url: 'services/connection/check/photoroom' },
	claid: { title: 'Claid', url: 'services/connection/check/claid' },
	storage: { title: 'Storage', url: 'services/connection/check/storage' },
};

const STATUS_META = {
	ok: { icon: 'mdi-check-circle', color: 'success' },
	warning: { icon: 'mdi-alert', color: 'warning' },
	error: { icon: 'mdi-close-circle', color: 'error' },
	skipped: { icon: 'mdi-minus-circle-outline', color: 'grey' },
};

export default {
	name: 'MarketplaceConnectionCheck',
	props: {
		value: { type: Boolean, default: false },
		service: { type: String, required: true, validator: v => Object.keys(SERVICES).includes(v) },
		// facoltativo: dati del form (anche non salvati) da verificare al posto di quelli salvati
		draft: { type: Object, default: null },
	},
	data() {
		return {
			loading: false,
			result: null,
			error: '',
		};
	},
	computed: {
		...mapGetters({
			getToken: 'profile/getToken',
			getUrl: 'settings/getUrl',
		}),
		title() {
			return SERVICES[this.service].title;
		},
		summary() {
			const statuses = this.result.checks.map(c => c.status);
			if (statuses.includes('error')) {
				return { type: 'error', text: `L'integrazione ${this.title} ha problemi: alcune operazioni non funzioneranno.` };
			}
			if (statuses.includes('warning')) {
				return { type: 'warning', text: `L'integrazione ${this.title} funziona, ma ci sono punti da controllare.` };
			}
			return { type: 'success', text: `L'integrazione ${this.title} è configurata correttamente e può lavorare.` };
		},
	},
	watch: {
		value(open) {
			if (open) {
				this.run();
			}
		},
		service() {
			this.result = null;
		},
	},
	methods: {
		...mapMutations({
			setLogout: 'profile/setLogout',
		}),
		statusMeta(status) {
			return STATUS_META[status] || STATUS_META.skipped;
		},
		async run() {
			if (this.loading) {
				return;
			}
			this.loading = true;
			this.error = '';
			try {
				const { data } = await axios.post(`${this.getUrl}${SERVICES[this.service].url}`, this.draft || {}, { headers: { Authorization: `Bearer ${this.getToken}` } });
				this.result = data.data;
			} catch (err) {
				this.result = null;
				if (err.response && err.response.status === 401) {
					this.setLogout();
					this.$router.push('/login');
					return;
				}
				// senza risposta (proxy in timeout, rete): il backend puo' aver finito comunque e messo il risultato in cache
				if (!err.response) {
					this.error = 'La verifica sta impiegando troppo tempo: riprova tra un minuto.';
					return;
				}
				const errors = err.response.data && err.response.data.error;
				this.error = (Array.isArray(errors) && errors[0]) || 'Verifica non riuscita, riprova.';
			} finally {
				this.loading = false;
			}
		},
	},
};
</script>
